import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CurrencyInput } from '@/components/ui/currency-input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft,
  Save,
  Play,
  Trash2,
  Plus,
  FileText,
  PlusCircle,
  Download,
  MoreVertical,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { exportToCSV, exportToExcel, generateFilename, formatCurrencyForExport, type ExportColumn } from '@/lib/export-utils';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import type { AdditionBatch, AdditionItem } from '@/lib/mock-additions-data';
import { dataService } from '@/services/data-service';
import { formatCurrency } from '@/lib/format-currency';
import { formatApiErrorMessage } from '@/lib/format-utils';

const getAdditionExportColumns = (): ExportColumn<AdditionItem>[] => [
  { label: 'Asset Code', key: 'assetCode' as keyof AdditionItem },
  { label: 'Asset Description', key: 'assetDescription' as keyof AdditionItem },
  { label: 'Category', key: 'category' as keyof AdditionItem },
  { label: 'Location', key: 'location' as keyof AdditionItem },
  { label: 'Value', key: 'value' as keyof AdditionItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Notes', key: 'notes' as keyof AdditionItem },
];

interface AdditionEntryScreenProps {
  batch: AdditionBatch;
  onClose: () => void;
  onSave: (batch: AdditionBatch) => void;
}

export function AdditionEntryScreen({
  batch: initialBatch,
  onClose,
  onSave,
}: AdditionEntryScreenProps): React.ReactElement {
  const navigate = useNavigate();
  
  // State for batch header
  const [batchReference, setBatchReference] = useState(initialBatch?.batchReference || '');
  const [dateOfRun, setDateOfRun] = useState(
    initialBatch?.dateOfRun || new Date().toISOString().split('T')[0]
  );
  const [periodType, setPeriodType] = useState<'current' | 'service'>(
    initialBatch?.periodType || 'current'
  );
  const [notes, setNotes] = useState(initialBatch?.notes || '');
  
  // State for addition items
  const [items, setItems] = useState<AdditionItem[]>(initialBatch?.items || []);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  
  // State for modals
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showProcessConfirm, setShowProcessConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Computed values
  const isReadOnly = initialBatch?.status === 'Completed';
  const totalValue = items.reduce((sum, item) => sum + item.value, 0);
  const allSelected = items.length > 0 && selectedItemIds.size === items.length;

  // Mark as having unsaved changes when any field changes (skip initial render)
  const [isInitialRender, setIsInitialRender] = useState(true);
  
  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
      return;
    }
    setHasUnsavedChanges(true);
  }, [batchReference, dateOfRun, periodType, notes, items]);

  const handleSave = async () => {
    // Validate required fields
    if (!batchReference.trim()) {
      toast.error('Reference is required', {
        description: 'Please enter a batch reference before saving.',
      });
      return;
    }

    if (!dateOfRun) {
      toast.error('Date is required', {
        description: 'Please select a date before saving.',
      });
      return;
    }

    if (items.length === 0) {
      toast.error('No assets selected', {
        description: 'Please add at least one asset to the addition batch.',
      });
      return;
    }

    const now = new Date().toISOString();
    
    const batch: AdditionBatch = {
      id: initialBatch?.id || `batch-${Date.now()}`,
      batchReference,
      dateOfRun,
      periodType,
      notes,
      items,
      status: 'In Progress',
      preparedBy: initialBatch?.preparedBy || 'Current User',
      preparedDate: initialBatch?.preparedDate || now,
      totalValue,
      currency: 'GBP',
      createdBy: initialBatch?.createdBy || 'Current User',
      createdDate: initialBatch?.createdDate || now,
      modifiedBy: 'Current User',
      modifiedDate: now,
    };

    try {
      onSave(batch);
      setHasUnsavedChanges(false);
      toast.success('Batch saved successfully');
    } catch (error) {
      console.error('Failed to save addition batch:', error);
      toast.error('Failed to save batch', {
        description: 'Please try again.',
      });
    }
  };

  const handleProcess = () => {
    // Validate required fields
    if (!batchReference.trim()) {
      toast.error('Reference is required', {
        description: 'Please enter a batch reference before processing.',
      });
      return;
    }

    if (!dateOfRun) {
      toast.error('Date is required', {
        description: 'Please select a date before processing.',
      });
      return;
    }

    if (items.length === 0) {
      toast.error('No assets selected', {
        description: 'Please add at least one asset to the addition batch.',
      });
      return;
    }

    // Validate that at least one item has a non-zero addition value
    const hasValidItems = items.some(item => item.value > 0);
    if (!hasValidItems) {
      toast.error('No addition values', {
        description: 'Please ensure at least one asset has a non-zero addition value.',
      });
      return;
    }

    setShowProcessConfirm(true);
  };

  const confirmProcess = async () => {
    try {
      const reference = batchReference;
      
      // Ensure batch is saved first
      const isServerGeneratedId = (id: string) => {
        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return guidRegex.test(id);
      };

      let batchId = initialBatch?.id;
      
      if (!isServerGeneratedId(initialBatch?.id || '')) {
        const now = new Date().toISOString();
        const batchToSave: AdditionBatch = {
          id: initialBatch?.id || `batch-${Date.now()}`,
          batchReference,
          dateOfRun,
          periodType,
          notes,
          items,
          status: 'In Progress',
          preparedBy: initialBatch?.preparedBy || 'Current User',
          preparedDate: initialBatch?.preparedDate || now,
          totalValue,
          currency: 'GBP',
          createdBy: initialBatch?.createdBy || 'Current User',
          createdDate: initialBatch?.createdDate || now,
          modifiedBy: 'Current User',
          modifiedDate: now,
        };
        const savedBatch = await dataService.createAdditionBatch(batchToSave);
        batchId = savedBatch.id;
      }
      
      // Call the backend PostBatch endpoint
      const result = await dataService.postAdditionBatch(batchId!, 'Current User');
      
      // Check for posting failures
      const postingResult = result?.postingResult;
      const hasCreationFailures = postingResult?.transactionCreationFailures?.length > 0;
      const hasPostingFailures = postingResult?.financialsPostingFailures?.length > 0;
      
      if (hasCreationFailures || hasPostingFailures) {
        const failureMessages: string[] = [];
        const errorDetails: string[] = [];
        
        if (hasCreationFailures) {
          const count = postingResult.transactionCreationFailures.length;
          failureMessages.push(`${count} transaction(s) failed to create`);
          postingResult.transactionCreationFailures.forEach((failure: { assetCode: string; errorMessage: string }) => {
            errorDetails.push(`Asset '${failure.assetCode}': ${formatApiErrorMessage(failure.errorMessage)}`);
          });
        }
        
        if (hasPostingFailures) {
          const count = postingResult.financialsPostingFailures.length;
          failureMessages.push(`${count} transaction(s) failed to post to Financials`);
          postingResult.financialsPostingFailures.forEach((failure: { assetCode: string; errorMessage: string }) => {
            errorDetails.push(`Asset '${failure.assetCode}': ${formatApiErrorMessage(failure.errorMessage)}`);
          });
        }
        
        const maxErrorsToShow = 3;
        const displayedErrors = errorDetails.slice(0, maxErrorsToShow);
        const remainingCount = errorDetails.length - maxErrorsToShow;
        let description = `Reference: ${reference}. ${failureMessages.join('. ')}.\n${displayedErrors.join('\n')}`;
        if (remainingCount > 0) {
          description += `\n...and ${remainingCount} more error(s)`;
        }
        
        toast.warning('Addition Completed with Warnings', {
          description,
          duration: 10000,
        });
      } else {
        toast.success('Addition Created Successfully', {
          description: `Reference: ${reference}`,
        });
      }
      
      setShowProcessConfirm(false);
      onClose(); // Close the entry screen and return to the landing page
    } catch (error) {
      console.error('Failed to process addition batch:', error);
      toast.error('Failed to process addition batch', {
        description: 'Please ensure you are in API mode and try again.',
      });
      setShowProcessConfirm(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  const confirmCancel = () => {
    setShowCancelConfirm(false);
    onClose();
  };

  const handleRemoveSelectedItems = () => {
    if (selectedItemIds.size === 0) return;
    setShowRemoveConfirm(true);
  };

  const confirmRemoveSelectedItems = () => {
    setItems(prevItems => prevItems.filter(item => !selectedItemIds.has(item.id)));
    setSelectedItemIds(new Set());
    setShowRemoveConfirm(false);
  };

  const handleAddMoreAssets = () => {
    // Navigate back to asset selection
    navigate('/transactions/additions', { 
      state: { openAssetSelection: true } 
    });
  };

  const handleUpdateItem = (itemId: string, value: number) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, value } : item
      )
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItemIds(new Set(items.map(item => item.id)));
    } else {
      setSelectedItemIds(new Set());
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    const newSelected = new Set(selectedItemIds);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItemIds(newSelected);
  };

  const handleExportCSV = () => {
    if (items.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      exportToCSV({
        data: items,
        columns: getAdditionExportColumns(),
        filename: generateFilename(`addition-batch-${batchReference || 'draft'}`, 'csv'),
      });

      toast.success('Export successful', {
        description: 'Addition batch exported to CSV',
      });
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  const handleExportExcel = () => {
    if (items.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      exportToExcel({
        data: items,
        columns: getAdditionExportColumns(),
        filename: generateFilename(`addition-batch-${batchReference || 'draft'}`, 'xlsx'),
        sheetName: 'Addition Details',
      });

      toast.success('Export successful', {
        description: 'Addition batch exported to Excel',
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: 'oklch(0.984 0.003 247.858)' }}>
      {/* Compact Header */}
      <div className="border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-sm flex-shrink-0">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={handleCancel} className="h-9 w-9">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-3">
                <PlusCircle className="h-5 w-5 text-primary" />
                <div>
                  <h1 className="text-xl font-bold">
                    {batchReference ? `Addition - ${batchReference}` : 'New Addition'}
                  </h1>
                  {isReadOnly && initialBatch?.postedDate ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Posted: {new Date(initialBatch.postedDate).toLocaleString()}
                    </p>
                  ) : initialBatch?.modifiedDate ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Last modified: {new Date(initialBatch.modifiedDate).toLocaleString()}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isReadOnly && (
                <>
                  <Button variant="outline" onClick={handleSave} className="h-9">
                    <Save className="mr-2 h-4 w-4" />
                    Save
                  </Button>
                  <Button onClick={handleProcess} className="h-9">
                    <Play className="mr-2 h-4 w-4" />
                    Process
                  </Button>
                </>
              )}
              {isReadOnly && (
                <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  Completed
                </Badge>
              )}
              {items.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="h-9 w-9">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleExportCSV}>
                      <Download className="mr-2 h-4 w-4" />
                      Export to CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExportExcel}>
                      <Download className="mr-2 h-4 w-4" />
                      Export to Excel
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto pb-20">
        <div className="space-y-4 p-4">
          {/* Header Details Card */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden rounded-lg">
            <div className="p-6">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Header Details
              </h3>
              <div className="grid grid-cols-12 gap-4">
                {/* Row 1: Reference, Date, Notes (right side) */}
                <div className="col-span-2">
                  <Label htmlFor="reference" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Reference *
                  </Label>
                  <Input
                    id="reference"
                    value={batchReference}
                    onChange={(e) => setBatchReference(e.target.value.slice(0, 10))}
                    placeholder="Enter reference..."
                    maxLength={10}
                    readOnly={isReadOnly}
                    className="h-9 placeholder:italic"
                    style={{ fontStyle: 'normal' }}
                    tabIndex={1}
                  />
                </div>

                {/* Date */}
                <div className="col-span-2">
                  <Label htmlFor="date" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Date *
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    value={dateOfRun}
                    onChange={(e) => setDateOfRun(e.target.value)}
                    readOnly={isReadOnly}
                    className="h-9 placeholder:italic"
                    style={{ fontStyle: 'normal' }}
                    tabIndex={2}
                  />
                </div>

                {/* Spacer for alignment */}
                <div className="col-span-2"></div>

                {/* Notes - Right side spanning 2 rows */}
                <div className="col-span-6 row-span-2">
                  <Label htmlFor="notes" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Notes
                  </Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value.slice(0, 400))}
                    placeholder="Enter any additional notes..."
                    rows={4}
                    maxLength={400}
                    readOnly={isReadOnly}
                    className="resize-none text-sm placeholder:italic"
                    style={{ fontStyle: 'normal' }}
                    tabIndex={3}
                  />
                  <p className="text-xs text-muted-foreground text-right mt-1">{notes.length}/400</p>
                </div>

                {/* Row 2: Period Type */}
                <div className="col-span-4">
                  <Label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">
                    Period Type
                  </Label>
                  <RadioGroup 
                    value={periodType} 
                    onValueChange={(value: 'current' | 'service') => setPeriodType(value)} 
                    disabled={isReadOnly}
                    className="flex gap-4"
                    data-readonly={isReadOnly ? '' : undefined}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="current" id="current" />
                      <Label htmlFor="current" className="font-normal cursor-pointer text-sm">
                        Current Period
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="service" id="service" />
                      <Label htmlFor="service" className="font-normal cursor-pointer text-sm">
                        Service Period
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>
            </div>
          </div>

          {/* Transaction Details Table */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden rounded-lg">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Transaction Details
                <Badge variant="secondary" className="ml-2">{items.length} {items.length === 1 ? 'Asset' : 'Assets'}</Badge>
              </h3>
              {!isReadOnly && (
                <div className="flex items-center gap-2">
                  {selectedItemIds.size > 0 && (
                    <Button type="button" variant="destructive" size="sm" onClick={handleRemoveSelectedItems} className="h-8">
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Remove Lines ({selectedItemIds.size})
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={handleAddMoreAssets} className="h-8">
                    <Plus className="mr-2 h-3.5 w-3.5" />
                    Add Assets
                  </Button>
                </div>
              )}
            </div>

            {items.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                    <TableRow className="border-t-2 border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                      {!isReadOnly && (
                        <TableHead className="h-12 font-semibold w-[40px]">
                          <Checkbox
                            checked={allSelected}
                            onCheckedChange={handleSelectAll}
                            aria-label="Select all"
                          />
                        </TableHead>
                      )}
                      <TableHead className="h-12 font-semibold">Asset Code</TableHead>
                      <TableHead className="h-12 font-semibold">Description</TableHead>
                      <TableHead className="h-12 font-semibold">Category</TableHead>
                      <TableHead className="h-12 font-semibold">Location</TableHead>
                      <TableHead className="h-12 font-semibold text-right">Current NBV</TableHead>
                      <TableHead className="h-12 font-semibold text-right bg-blue-50/50 dark:bg-blue-950/20">Addition Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow 
                        key={item.id} 
                        className={`
                          ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                          hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800
                        `}
                      >
                        {!isReadOnly && (
                          <TableCell className="w-[40px]">
                            <Checkbox
                              checked={selectedItemIds.has(item.id)}
                              onCheckedChange={(checked) => handleSelectItem(item.id, checked as boolean)}
                              aria-label={`Select ${item.assetCode}`}
                            />
                          </TableCell>
                        )}
                        <TableCell className="font-medium text-sm">{item.assetCode}</TableCell>
                        <TableCell className="text-sm">{item.assetDescription}</TableCell>
                        <TableCell className="text-sm">{item.category}</TableCell>
                        <TableCell className="text-sm">{item.location}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(item.currentNBV)}</TableCell>
                        <TableCell className="text-right">
                          <CurrencyInput
                            value={item.value}
                            onChange={(value) => handleUpdateItem(item.id, value)}
                            className="w-full text-right text-sm font-medium"
                            readOnly={isReadOnly}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="rounded-full bg-muted p-3 mb-4">
                  <FileText className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg mb-1">No Assets Added</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add assets to this addition batch to get started
                </p>
                {!isReadOnly && (
                  <Button variant="outline" size="sm" onClick={handleAddMoreAssets}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Assets
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Bottom Footer */}
      {items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-t border-slate-200 dark:border-slate-800 shadow-lg z-10">
          <div className="px-6 py-3">
            <div className="flex justify-end items-center gap-4 max-w-7xl ml-auto">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Current NBV</span>
                <span className="text-base font-bold text-slate-900 dark:text-slate-100">{formatCurrency(0)}</span>
              </div>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">NBV after Addition</span>
                <span className="text-base font-bold text-slate-900 dark:text-slate-100">{formatCurrency(totalValue)}</span>
              </div>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Adjustment</span>
                <span className="text-base font-bold text-green-600 dark:text-green-400">+{formatCurrency(totalValue)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modals */}
      <ConfirmationModal
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        onConfirm={confirmCancel}
        title="Discard Changes?"
        description="You have unsaved changes. Are you sure you want to leave? All changes will be lost."
        confirmText="Discard Changes"
        cancelText="Continue Editing"
        type="warning"
      />

      <ConfirmationModal
        open={showProcessConfirm}
        onOpenChange={setShowProcessConfirm}
        onConfirm={confirmProcess}
        title="Process Addition Batch"
        description={
          <div className="space-y-2">
            <p>This will process the addition batch and create transactions.</p>
            <div className="mt-4 p-3 bg-muted rounded-md space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Assets:</span>
                <span className="font-medium">{items.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Value:</span>
                <span className="font-medium">{formatCurrency(totalValue)}</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-3">This action cannot be undone.</p>
          </div>
        }
        confirmText="Process Batch"
        cancelText="Review Again"
        type="success"
      />

      <ConfirmationModal
        open={showRemoveConfirm}
        onOpenChange={setShowRemoveConfirm}
        onConfirm={confirmRemoveSelectedItems}
        title="Remove Selected Assets"
        description={`Are you sure you want to remove ${selectedItemIds.size} selected asset(s) from this batch?`}
        confirmText="Remove Assets"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}
